// «الصيانة» — one row per branch (or per maintenance location with no branch), sortable.
// Overdue work is highlighted; a suggested (unconfirmed) mapping carries «مطابقة مقترحة».
import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MAINT } from "@/constants/testIds";
import { bucketName, fmtDuration, fmtInt, fmtPct, fmtSar } from "./maintText";

const COLS = [
    ["open_requests", (r) => r.open_requests],
    ["open_work_orders", (r) => r.open_work_orders],
    ["overdue", (r) => r.overdue],
    ["awaiting_close", (r) => r.awaiting_close],
    ["awaiting_branch", (r) => r.awaiting_branch_confirmation],
    ["met", (r) => r.met_target?.pct],
    ["ttc", (r) => r.time_to_complete?.avg_minutes],
    ["repeat", (r) => r.repeat_failures],
    ["parts_cost", (r) => r.parts_cost],
];

function SortHead({ col, label, sort, onSort, start }) {
    const active = sort.col === col;
    const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
    return (
        <TableHead data-testid={MAINT.branchCol(col)} onClick={() => onSort(col)} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                   className={`cursor-pointer select-none whitespace-nowrap text-xs ${start ? "text-start" : "text-end"}`}>
            {/* the icon changes with the sort: the label keeps its own element (translated page, incident 2026-10-24) */}
            <span className={`inline-flex items-center gap-1 ${start ? "" : "flex-row-reverse"}`}>
                <Icon className={`h-3.5 w-3.5 ${active ? "text-[color:var(--brand-orange)]" : "text-[color:var(--ink-faint)]"}`} />
                <span>{label}</span>
            </span>
        </TableHead>
    );
}

export function BranchTable({ rows, t, lang, onOpen }) {
    const [sort, setSort] = useState({ col: "overdue", dir: "desc" });
    const onSort = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: col === "branch" ? "asc" : "desc" }));
    const sorted = useMemo(() => {
        const get = sort.col === "branch" ? (r) => bucketName(r, lang) : (COLS.find(([k]) => k === sort.col) || COLS[0])[1];
        const sign = sort.dir === "asc" ? 1 : -1;
        return [...(rows || [])].sort((a, b) => {
            const va = get(a);
            const vb = get(b);
            if (va === vb) return bucketName(a, lang).localeCompare(bucketName(b, lang), lang);
            if (va === null || va === undefined) return 1;
            if (vb === null || vb === undefined) return -1;
            return (typeof va === "string" ? va.localeCompare(vb, lang) : va - vb) * sign;
        });
    }, [rows, sort, lang]);

    const cell = (r, k) => {
        switch (k) {
            case "met": return fmtPct(r.met_target?.pct, lang);
            case "ttc": return fmtDuration(r.time_to_complete?.avg_minutes, t);
            case "parts_cost": return fmtSar(r.parts_cost, lang, t);
            case "awaiting_branch": return fmtInt(r.awaiting_branch_confirmation, lang);
            case "repeat": return fmtInt(r.repeat_failures, lang);
            default: return fmtInt(r[k], lang);
        }
    };

    return (
        <Card className="overflow-hidden rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)]">
            <div className="overflow-x-auto scrollbar-thin">
                <Table data-testid={MAINT.branchTable}>
                    <TableHeader>
                        <TableRow>
                            <SortHead col="branch" label={t("mt_col_branch")} sort={sort} onSort={onSort} start />
                            {COLS.map(([k]) => <SortHead key={k} col={k} label={t(`mt_col_${k}`)} sort={sort} onSort={onSort} />)}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sorted.map((r) => (
                            <TableRow key={r.bucket} data-testid={MAINT.branchRow(r.bucket)} onClick={() => onOpen(r)}
                                      data-overdue={r.overdue ? "true" : "false"}
                                      className={`cursor-pointer ${r.overdue ? "bg-[color:var(--danger-soft)]/50" : ""}`}>
                                <TableCell className="whitespace-nowrap text-start">
                                    <span className="block font-semibold text-[color:var(--ink)]">{bucketName(r, lang)}</span>
                                    {r.mapping_state === "suggested" || r.mapping_state === "mixed" ? (
                                        <Badge data-testid={MAINT.suggestedBadge} variant="warning" className="mt-0.5 px-2 py-0 text-[10px]">
                                            <span>{t("mt_suggested")}</span>
                                        </Badge>
                                    ) : r.mapping_state === "unmapped" ? (
                                        <Badge data-testid={MAINT.unmappedBadge} variant="outline" className="mt-0.5 px-2 py-0 text-[10px]">
                                            <span>{t("mt_unmapped")}</span>
                                        </Badge>
                                    ) : null}
                                </TableCell>
                                {COLS.map(([k]) => (
                                    <TableCell key={k} data-testid={MAINT.branchCell(r.bucket, k)}
                                               className={`whitespace-nowrap text-end tnum ${k === "overdue" && r.overdue ? "font-extrabold text-[color:var(--danger)]" : "text-[color:var(--ink)]"}`}>
                                        {cell(r, k)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {!sorted.length ? (
                            <TableRow><TableCell colSpan={COLS.length + 1} className="py-8 text-center text-sm text-[color:var(--ink-muted)]">{t("mt_no_rows")}</TableCell></TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </div>
        </Card>
    );
}

export function TechniciansCard({ rows, t, lang }) {
    return (
        <Card data-testid={MAINT.technicians} className="rounded-2xl border-[color:var(--border-soft)] p-4 shadow-[var(--shadow-sm)]">
            <h3 className="mb-3 text-sm font-extrabold text-[color:var(--ink)]">{t("mt_tech_title")}</h3>
            {!rows?.length ? (
                <p className="text-xs text-[color:var(--ink-muted)]">{t("mt_tech_none")}</p>
            ) : (
                <ul className="divide-y divide-[color:var(--separator)]">
                    {rows.map((r) => (
                        <li key={r.user_id} data-testid={MAINT.techRow(r.user_id)} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 py-2">
                            <span className="min-w-0 truncate text-sm font-semibold text-[color:var(--ink)]"><bdi>{r.name || t("mt_tech_unknown")}</bdi></span>
                            <span className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold tnum">
                                <span className="rounded-full bg-[color:var(--brand-sky-soft)] px-2 py-0.5 text-[color:var(--ink)]">
                                    <span>{t("mt_tech_open")}</span> <span>{fmtInt(r.open, lang)}</span>
                                </span>
                                {r.overdue ? (
                                    <span className="rounded-full bg-[color:var(--danger-soft)] px-2 py-0.5 text-[color:var(--danger-soft-fg)]">
                                        <span>{t("mt_tech_overdue")}</span> <span>{fmtInt(r.overdue, lang)}</span>
                                    </span>
                                ) : null}
                                <span className="rounded-full bg-[color:var(--success-soft)] px-2 py-0.5 text-[color:var(--success-soft-fg)]">
                                    <span>{t("mt_tech_completed")}</span> <span>{fmtInt(r.completed, lang)}</span>
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}
